import { showError } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router-dom";

import { SuiviHopeForm, SuiviWalterForm } from "../../..";
import { EFormePersonne, SuiviApi, SuiviHopeEditDto } from "../../../../../api";
import { ERoutes } from "../../../../../AppRouter";
import { useAuth, useDialog } from "../../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { exportFile } from "../../../../../utils";

export interface FormateurOtherSuiviDetailPageProps {}

export const FormateurOtherSuiviDetailPage: React.FunctionComponent<FormateurOtherSuiviDetailPageProps> = props => {
  const { t } = useTl();
  const api = useApiService(SuiviApi);
  const { user } = useAuth();
  const history = useHistory();
  const { id, state, idSuivi } = useParams<{
    id: string;
    tab: string;
    state: string;
    idSuivi: string;
  }>();
  const editMode = React.useMemo(() => state === "edit", [state]);

  const { data, loading, saveItem, saving, deleteItem, deleting, validationErrors, refresh } = useCrudApi<
    SuiviHopeEditDto
  >(
    React.useMemo(
      () => ({
        getApiFn: async () => {
          const suivi = await api.suiviGetSuiviFormateur({
            id: +idSuivi,
            idformateur: +id
          });
          if (+idSuivi <= 0) {
            suivi.idsuivi = 0;
            suivi.idrealisateurSuivi = user.iduser;
            suivi.encodeur = user.nom + " " + user.prenom;
            suivi.dateSuivi = new Date();
          }
          return suivi;
        },
        closeCfs: false,
        saveApiFn: d => api.suiviSaveSuiviHope({ SuiviHopeEditDto: d }),
        onSavedRoute: d => {
          return `${ERoutes.formateur}/${id}/suivi/autres/${state}/${d.idsuivi}`;
        },
        getDeps: [idSuivi],
        deleteApiFn: d => api.suiviDeleteSuiviHope({ id: d.idsuivi }),
        onDeletedRoute: () => `${ERoutes.formateur}/${id}/suivi/autres/${state}`
      }),
      [idSuivi, api, id, user.iduser, user.nom, user.prenom, state]
    )
  );

  const onCancel = React.useCallback(() => history.push(`${ERoutes.formateur}/${id}/suivi/autres/${state}`), [
    history,
    id,
    state
  ]);

  const [rowLoading, setRowLoading] = React.useState(null);
  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(idsuiviFichier);
          await api.suiviDeleteSuiviFichier({
            id: idsuiviFichier
          });
          refresh();
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, refresh, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      try {
        setRowLoading(idsuiviFichier);
        const file = await api.suiviDownloadSuiviFichier({
          id: idsuiviFichier
        });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  return !!data?.idcentre ? (
    <SuiviWalterForm
      data={data}
      suiviId={+idSuivi}
      saveItem={saveItem}
      loading={loading}
      saving={saving}
      editMode={editMode && !data?.fromSmile}
      deleteItem={deleteItem}
      deleting={deleting}
      onCancel={onCancel}
      validationErrors={validationErrors}
      fieldsetTitle={t(ETLCodes.Suivi)}
      deleteFichier={deleteFichier}
      downloadFichier={downloadFichier}
      rowLoading={rowLoading}
      refreshData={refresh}
      forme={EFormePersonne.Formateur}
    />
  ) : (
    <SuiviHopeForm
      data={data}
      suiviId={+idSuivi}
      saveItem={saveItem}
      loading={loading}
      saving={saving}
      editMode={editMode && !data?.fromSmile}
      deleteItem={deleteItem}
      deleting={deleting}
      onCancel={onCancel}
      validationErrors={validationErrors}
      fieldsetTitle={t(ETLCodes.Suivi)}
      deleteFichier={deleteFichier}
      downloadFichier={downloadFichier}
      rowLoading={rowLoading}
      refreshData={refresh}
      forme={EFormePersonne.Formateur}
    />
  );
};
